<script setup lang="ts">
    import AppModalContent from '@/components/modal/AppModalContent.vue';
    import { computed, onUnmounted } from 'vue';
    import { useSurgicalGuideOrderDialog } from '@/views/plan/useSurgicalGuideOrderDialog';
    import { PLANS_TARGETS } from '@/domain/analytics/analyticsTargets';

    const vm = useSurgicalGuideOrderDialog();

    onUnmounted(() => vm.reset());

    const label = computed(() => (vm.isRequestingCrNumber ? 'Request CR Number' : 'Order Guide'));
</script>

<template>
    <app-modal :model-value="vm.isOpen" persistent max-width="700">
        <v-card class="no-shadow modal-dialog">
            <app-modal-title class="modal-title" @close="vm.cancelOrder">
                {{ label }}
            </app-modal-title>

            <app-modal-content>
                <div class="modal-message">
                    <img alt="Formuslabs Logo" src="@/assets/images/guide-order.svg" />
                    <p>
                        Please check your surgery date is three weeks from when you order your OSSIS
                        Femoral Neck Cut Guide
                    </p>
                </div>
                <v-alert
                    v-if="!vm.threeWeeksWarning"
                    :closable="false"
                    colored-border
                    class="d-flex align-center alert-info"
                    color="info"
                    variant="tonal"
                >
                    <v-icon icon="mdi-information-outline" class="align-self-center" />
                    <p class="ml-4 alert-message">
                        Important: Please note that your surgery date must be at least 3 weeks from
                        your Femoral Neck Guide order date. This is to ensure timely delivery, as
                        guides require a minimum of 21 days for processing.
                    </p>
                </v-alert>

                <v-alert
                    v-if="vm.threeWeeksWarning"
                    :closable="false"
                    colored-border
                    class="d-flex align-center alert-warning"
                    color="warning"
                    variant="tonal"
                >
                    <v-icon icon="mdi-alert-outline" class="align-self-center" />
                    <p class="ml-4 alert-message">
                        Warning: Surgery Date is less than 3 weeks from OSSIS Neck Guide order date.
                        Please review your surgery date.
                    </p>
                </v-alert>

                <div class="modal-surgery-date-form pt-6">
                    <div class="surgery-date-title">Surgery Date</div>

                    <app-text-input
                        v-model="vm.form.surgeryDate"
                        type="date"
                        :disabled="vm.surgeryDateDisabled"
                        input-id="case-surgery-date"
                        :error-messages="vm.form.surgeryDate ? '' : 'Please provide a valid date'"
                    />
                </div>

                <div v-if="vm.showCrNumber" class="modal-cr-form">
                    <div class="cr-form-title mb-3">ZB Custom Response Number</div>
                    <app-text-input
                        v-model="vm.form.crNumber"
                        input-id="case-cr-number"
                        :error-messages="
                            vm.crNumberInvalid
                                ? 'CR number must contain only numbers or must be left blank'
                                : ''
                        "
                    />
                </div>

                <div class="modal-surgical-approach-form">
                    <div class="surgical-approach-form-title mb-3">Surgical Approach</div>
                    <app-toggle
                        v-model="vm.form.surgicalApproachId"
                        :options="vm.surgicalApproachOptions"
                    />
                </div>

                <v-alert
                    v-if="vm.submitError"
                    :closable="false"
                    colored-border
                    class="d-flex align-center alert-error mt-6"
                    color="error"
                    variant="tonal"
                    data-testid="order-submit-error"
                >
                    <v-icon icon="mdi-alert-circle-outline" class="align-self-center" />
                    <p class="ml-4 alert-message">{{ vm.submitError }}</p>
                </v-alert>
            </app-modal-content>

            <app-modal-actions
                class="justify-center"
                :data-analytics-id="PLANS_TARGETS.orderModalButtons"
            >
                <app-button-secondary width="150" class="mr-5" @click="vm.cancelOrder">
                    Close
                </app-button-secondary>
                <app-button-primary
                    :color="vm.disableSubmit ? 'primary' : 'green'"
                    :disabled="vm.disableSubmit || vm.isSaving"
                    :loading="vm.isSaving"
                    @click="vm.submitOrder"
                >
                    {{ label }}
                </app-button-primary>
            </app-modal-actions>
        </v-card>
    </app-modal>
</template>

<style scoped lang="scss">
    .modal-dialog {
        padding-bottom: 20px !important;
    }

    .v-alert :deep(.v-alert__content) {
        display: flex;
    }

    .modal-title {
        text-align: center;
    }

    .modal-title :deep(h3) {
        font-weight: bold;
        color: black;
        padding-left: 55px;
    }

    .modal-message {
        text-align: center;
        font-size: 18px;
        margin-bottom: 16px;
    }

    .modal-message img {
        margin-bottom: 14px;
    }

    .modal-surgical-approach-form {
        margin-top: 8px;
        display: flex;
    }

    .surgical-approach-form-title {
        text-wrap: nowrap;
        font-size: 16px;
        margin-top: 8px;
        margin-right: 16px;
    }

    .alert-message {
        font-weight: bold;
    }

    .modal-surgery-date-form {
        display: flex;
    }

    .surgery-date-title {
        text-wrap: nowrap;
        margin-top: 8px;
        margin-right: 16px;
    }
</style>
